/* oxlint-disable react/static-components -- these render APIs intentionally receive component references */
import { ParentSize } from '@visx/responsive'
import moment from 'moment'
import TwoTabDashboard from 'app/components/analytics/TwoTabDashboard'
import Spinner from 'app/components/Spinner'
import DateGroupSelector from 'app/components/viz/DateGroupSelector'
import GraphHeader from 'app/components/viz/GraphHeader'
import GroupedBarStackGraph from 'app/components/viz/graphs/GroupedBarStackGraph'
import { PeriodTrendList } from 'app/components/analytics/PeriodTrendList'
import { CHANNEL_HUES, SMC_CHANNEL_GROUP_HUES } from 'lib/chartColors'
import { NewPatientsRow } from 'types/analytics'
import { Jsonify } from 'lib/toJSON'
import type { UseNewPatientsReturn } from './useNewPatients'

export type NewPatientsShadcnComponentProps = UseNewPatientsReturn

export function NewPatientsShadcnComponent(props: NewPatientsShadcnComponentProps) {
  const {
    filteredLocationRows,
    tableColumns,
    stats,
    dateChangeHandler,
    setFilters,
    channel,
    isSMC,
    barsGroupedBy,
    setBarsGroupedBy,
    dayMetrics,
    newPatientsGrouped,
    scheduledGrouped,
    summaryMetrics,
    dontCompare,
    isComparingToGoals,
    budgetByMonth,
    groupByChannelOrTreatment,
    graphContainerRef,
    isRequestInFlight,
    fromDate,
    toDate,
    limitDateRangeToMonth,
  } = props

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const Graphs = () => (
    <div className="pt-4">
      <GraphHeader
        leftSection={[{ title: `New Patients`, count: null }]}
        rightSection={[
          <DateGroupSelector
            key={2}
            defaultGroup={barsGroupedBy}
            onGroupChange={(group) => setBarsGroupedBy(group)}
            disableDays={dayMetrics ? dayMetrics.length > 31 : false}
            disableWeeks={dayMetrics ? dayMetrics.length > 31 || dayMetrics.length <= 7 : false}
            disableMonths={dayMetrics ? dayMetrics.length <= 31 : false}
          />,
        ]}
      />
      <div className="overflow-x-auto">
        <div ref={graphContainerRef} className="relative h-[500px] min-w-[1080px]">
          <ParentSize>
            {({ width, height }) => {
              let data2Value: { day: string; tooltipDisplay?: string; values: { [key: string]: number } }[] = []
              if (!dontCompare) {
                if (isComparingToGoals && Object.keys(budgetByMonth).length > 0) {
                  data2Value = (newPatientsGrouped?.byChannel ?? []).map((d) => {
                    const monthKey = moment(d.day, barsGroupedBy === `months` ? `MMM YYYY` : `MMM D YYYY`).format(
                      `YYYY-MM`,
                    )
                    const goalValue = budgetByMonth[monthKey] ?? 0
                    return {
                      day: d.day,
                      tooltipDisplay: `Goal`,
                      values: goalValue ? { 'New Patient Goal': goalValue } : ({} as Record<string, number>),
                    }
                  })
                } else if (!isComparingToGoals) {
                  data2Value =
                    groupByChannelOrTreatment === `channel`
                      ? (newPatientsGrouped?.byChannelYoY ?? [])
                      : (newPatientsGrouped?.byTreatment ?? [])
                }
              }
              if (!width || !height) {
                return (
                  <div className="flex h-full items-center justify-center">
                    <Spinner size={10} />
                  </div>
                )
              }
              return (
                <>
                  {isRequestInFlight && (
                    <div
                      style={{
                        width,
                        height: height + 50,
                      }}
                      className="absolute left-0 top-0 z-10 flex items-center justify-center bg-surface bg-opacity-70"
                    >
                      <Spinner size={10} />
                    </div>
                  )}
                  <GroupedBarStackGraph
                    allKeys={
                      groupByChannelOrTreatment === `channel`
                        ? [`Digital Marketing`, `Traditional Marketing`, `Referral Marketing`, `None`]
                        : []
                    }
                    width={width}
                    height={height}
                    data={
                      groupByChannelOrTreatment === `channel`
                        ? (newPatientsGrouped?.byChannel ?? [])
                        : (newPatientsGrouped?.byTreatment ?? [])
                    }
                    comparedData={data2Value}
                    totals={
                      groupByChannelOrTreatment === `channel`
                        ? summaryMetrics?.byChannel?.reduce((acc: any, curr: any) => {
                            acc[curr.channel] = curr.count
                            return acc
                          }, {})
                        : undefined
                    }
                    tickFormat={barsGroupedBy === `months` ? `MMM YYYY` : `MMM D`}
                    now={
                      moment().isBetween(fromDate, toDate, `day`, `[]`)
                        ? moment().format(barsGroupedBy === `months` ? `MMM YYYY` : `MMM D YYYY`)
                        : null
                    }
                    scheduledData={scheduledGrouped}
                    colorMap={isSMC ? SMC_CHANNEL_GROUP_HUES : CHANNEL_HUES}
                  />
                </>
              )
            }}
          </ParentSize>
        </div>
      </div>
    </div>
  )

  return (
    <TwoTabDashboard<Jsonify<NewPatientsRow>>
      title="New Patients Overview"
      defaultColumns={tableColumns as any}
      filteredMetrics={filteredLocationRows}
      dateChangeHandler={dateChangeHandler}
      graph={<Graphs />}
      mobileGraphReplacement={
        <PeriodTrendList title="New Patients by month" periods={newPatientsGrouped?.byChannel ?? []} />
      }
      limitDateRangeToMonth={limitDateRangeToMonth}
      allowFutureDates={true}
      defaultDateRange="last_6_months"
      defaultCompareSelectorPreset="previous_year"
      defaultChannel={channel}
      compareSelectorBlacklist={isSMC ? [`custom`, `previous_period`] : [`custom`, `goals`, `previous_period`]}
      stats={stats}
      handleFiltersChange={setFilters}
      hideFooter={false}
    />
  )
}
